import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  getDocs,
  startAfter,
  limit,
  onSnapshot,
  increment,
  serverTimestamp
} from "firebase/firestore";
import { db, auth } from "../firebase";
import Reactions from "../community/Reactions";
import axios from "axios";
import "./community.css";

/* ==========================================================
   AI SMART AUTO-ENGAGE ENGINE
   ========================================================== */

const careerKeywords = [
  "resume", "interview", "apply", "job", "career", "promotion",
  "salary", "offer", "linkedin", "experience", "role",
  "interviewer", "hiring manager", "opportunity",
  "skills", "certification", "work", "professional",
];

function shouldAIRespond(text, category, replies) {
  const lower = text.toLowerCase().trim();

  if (lower.length < 20) return false;

  const hasCareerIntent =
    lower.includes("?") ||
    careerKeywords.some((k) => lower.includes(k));

  if (!hasCareerIntent) return false;

  const aiAlready = replies.some((r) => r.isAI === true);
  if (aiAlready && !lower.includes("follow")) return false;

  return true;
}

function routeToTool(text, category) {
  if (category === "resume-reviews") return "resume";
  if (category === "interview-tips") return "interview";
  return "career";
}

function formatAIResponse(raw) {
  return (
    raw +
    "\n\nIf you'd like more tailored guidance, share your experience level or target role."
  );
}

/* ==========================================================
   THREAD COMPONENT
   ========================================================== */

function Thread() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
 
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState("");

 
  const REPLIES_PAGE_SIZE = 20;
  const [lastDoc, setLastDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [localReplies, setLocalReplies] = useState([]);


  const apiBase = process.env.REACT_APP_API_URL;

 
  const reactionMap = {
    "👍": "like",
    "😂": "funny",
    "❤️": "heart",
    "🔥": "fire",
    "👏": "helpful",
  };


 // Safe timestamp parser
const getReplyTime = (item) => {

  const ts = item?.createdAt;

  // Firestore Timestamp object
  if (ts?.seconds) {
    return new Date(ts.seconds * 1000);
  }

  // Numeric timestamp (Date.now format)
  if (typeof ts === "number") {
    return new Date(ts);
  }

  // Optimistic UI timestamp (new Date())
  if (ts instanceof Date) {
    return ts;
  }

  // clientCreatedAt fallback
  if (item?.clientCreatedAt) {
    return new Date(item.clientCreatedAt);
  }

  return null;
};

  /* ==========================================================
     1️⃣ LOAD THREAD POST
     ========================================================== */
  useEffect(() => {
    async function loadPost() {
      try {
        const ref = doc(db, "community_posts", id);
        const snap = await getDoc(ref);

        if (snap.exists()) {
          const data = snap.data();
   
          setPost({
            id: snap.id,
            ...data,
            reactions: data.reactions || {
              like: 0,
              celebrate: 0,
              insightful: 0,
              support: 0,
              funny: 0,
            },
          });
        }
      } catch (err) {
        console.error("Error loading post:", err);
      }
    }
    loadPost();
  }, [id]);

  /* ==========================================================
     2️⃣ LOAD REPLIES (PAGINATED)
     ========================================================== */
  async function loadReplies(initial = false) {
    try {
      if (initial) {
        setReplies([]);
        setLastDoc(null);
      }

      setLoadingMore(true);

      let qBase = query(
        collection(db, "community_posts", id, "replies"),
        orderBy("createdAt", "asc"),
        limit(REPLIES_PAGE_SIZE)
      );

      if (!initial && lastDoc) {
        qBase = query(
          collection(db, "community_posts", id, "replies"),
          orderBy("createdAt", "asc"),
          startAfter(lastDoc),
          limit(REPLIES_PAGE_SIZE)
        );
      }

      const snap = await getDocs(qBase);
  
      const list = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      setReplies((prev) => (initial ? list : [...prev, ...list]));
      setLastDoc(snap.docs[snap.docs.length - 1] || null);

      setLoading(false);
      setLoadingMore(false);
    } catch (err) {
      console.error("Error loading replies:", err);
    }
  }

  useEffect(() => {

  const q = query(
  collection(db, "community_posts", id, "replies"),
  orderBy("createdAt", "asc"),
  limit(100)
);

  const unsubscribe = onSnapshot(q, (snapshot) => {

    const list = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    setReplies(list);
    setLocalReplies(list);
    setLoading(false);

  });

  return () => unsubscribe();

}, [id]);


  /* ==========================================================
     3️⃣ DELETE ENTIRE THREAD (Author Only)
     ========================================================== */
  const deleteThread = async () => {
    const user = auth.currentUser;
    if (!user) return;

    const confirmed = window.confirm(
      "Delete this post and ALL replies? This cannot be undone."
    );
    if (!confirmed) return;

    try {
      // Delete replies
      const repliesRef = collection(db, "community_posts", id, "replies");
      const snap = await getDocs(repliesRef);

      const batchDeletes = snap.docs.map((r) =>
        deleteDoc(doc(db, "community_posts", id, "replies", r.id))
      );
      await Promise.all(batchDeletes);

      // Delete post
      await deleteDoc(doc(db, "community_posts", id));

      navigate("/community");
    } catch (err) {
      console.error("Thread delete error:", err);
    }
  };

  /* ==========================================================
     4️⃣ ADD REPLY REACTION
     ========================================================== */
  const addReplyReaction = async (replyId, emoji) => {
  const uid = auth.currentUser?.uid;
  if (!uid) return;

  const key = reactionMap[emoji];

  setLocalReplies(prev =>
    prev.map(r => {
      if (r.id !== replyId) return r;

      const current = r.userReactions?.[uid];

      // REMOVE
      if (current === key) {
        return {
          ...r,
          userReactions: { ...r.userReactions, [uid]: null },
          replyReactions: {
            ...r.replyReactions,
            [key]: Math.max((r.replyReactions?.[key] || 1) - 1, 0),
          },
        };
      }

      // SWITCH
      if (current && current !== key) {
        return {
          ...r,
          userReactions: { ...r.userReactions, [uid]: key },
          replyReactions: {
            ...r.replyReactions,
            [current]: Math.max((r.replyReactions?.[current] || 1) - 1, 0),
            [key]: (r.replyReactions?.[key] || 0) + 1,
          },
        };
      }

      // ADD
      return {
        ...r,
        userReactions: { ...r.userReactions, [uid]: key },
        replyReactions: {
          ...r.replyReactions,
          [key]: (r.replyReactions?.[key] || 0) + 1,
        },
      };
    })
  );

  const ref = doc(db, "community_posts", id, "replies", replyId);
  const current = localReplies.find(r => r.id === replyId)
    ?.userReactions?.[uid];

  // Firestore sync
  if (current === key) {
    await updateDoc(ref, {
      [`replyReactions.${key}`]: increment(-1),
      [`userReactions.${uid}`]: null,
    });
  } else if (current && current !== key) {
    await updateDoc(ref, {
      [`replyReactions.${current}`]: increment(-1),
      [`replyReactions.${key}`]: increment(1),
      [`userReactions.${uid}`]: key,
    });
  } else {
    await updateDoc(ref, {
      [`replyReactions.${key}`]: increment(1),
      [`userReactions.${uid}`]: key,
    });
  }
};


  /* ==========================================================
     5️⃣ DELETE REPLY
     ========================================================== */
  const deleteReply = async (replyId) => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      await deleteDoc(doc(db, "community_posts", id, "replies", replyId));

    } catch (err) {
      console.error("Delete reply error:", err);
    }
  };

/* ==========================================================
   6️⃣ SUBMIT REPLY (+ AI AUTO-ENGAGE)
   ========================================================== */
const submitReply = async () => {
  if (!replyText.trim() || !auth.currentUser) return;

  const text = replyText;

  const userRef = doc(db, "users", auth.currentUser.uid);
const userSnap = await getDoc(userRef);

const username =
  userSnap.exists() && userSnap.data().username
    ? userSnap.data().username
    : auth.currentUser.displayName || "User";

const optimisticReply = {
  id: `temp-${Date.now()}`,
  text,
  username,
  userId: auth.currentUser.uid,
    createdAt: serverTimestamp(),
    replyReactions: {},
    isAI: false,
  };

  // 🔥 Optimistic UI
  setLocalReplies(prev => [...prev, optimisticReply]);
  setReplyText("");

  try {
    // Save user reply
    await addDoc(collection(db, "community_posts", id, "replies"), {
      text,
      username: optimisticReply.username,
      userId: optimisticReply.userId,
      createdAt: serverTimestamp(),
      clientCreatedAt: serverTimestamp(),
      replyReactions: {},
      isAI: false,
    });

   // 🔔 Notify thread owner
if (post?.userId && post.userId !== auth.currentUser.uid) {

  try {

    await axios.post(`${apiBase}/api/create-notification`, {
      uid: post.userId,
      title: "New reply to your post",
      body: `${auth.currentUser.displayName || "Someone"} replied to your discussion`,
      type: "community_reply",
      link: `/community/thread/${id}`
    });

  } catch (err) {
    console.error("Notification error:", err);
  }

}

    // --- AI DECISION ENGINE ---
    const shouldRespondNow = shouldAIRespond(
      text,
      post.category,
      localReplies
    );

    if (!shouldRespondNow) return;

    const tool = routeToTool(text, post.category);
    let endpoint = "/api/ai-career-advisor";

    if (tool === "resume") endpoint = "/api/ai-resume-critique";
    if (tool === "interview") endpoint = "/api/ai-interview-coach";

    await new Promise(r =>
      setTimeout(r, 3500 + Math.random() * 3000)
    );

    const aiRes = await axios.post(`${apiBase}${endpoint}`, {
      topic: text,
      question: text,
      resume: text,
    });

    const raw =
      aiRes.data?.advice ||
      aiRes.data?.coach ||
      aiRes.data?.analysis ||
      null;

    if (!raw) return;

    const clean = raw.trim();
    if (clean.length < 25) return;

    const badPhrases = [
      "error",
      "unable",
      "cannot",
      "not sure",
      "uncertain",
      "invalid",
      "as an ai",
    ];

    if (
      badPhrases.some(p =>
        clean.toLowerCase().includes(p)
      )
    )
      return;

    const aiReply = formatAIResponse(clean);

    // Save AI reply
    await addDoc(collection(db, "community_posts", id, "replies"), {
      text: aiReply,
      username: "EmployPilot AI",
      userId: "ai_bot",
      createdAt: serverTimestamp(),
      clientCreatedAt: Date.now(),
      isAI: true,
      replyReactions: {
        like: 0,
        funny: 0,
        heart: 0,
        fire: 0,
        helpful: 0,
      },
    });

    await updateDoc(doc(db, "community_posts", id), {
      updatedAt: new Date(),
    });
  } catch (err) {
    console.error("Reply error:", err);
  }
};

  /* ==========================================================
     UI
     ========================================================== */

  if (!post) return <div className="forum-loading">Loading post...</div>;

  return (
    <div className="thread-wrapper">
      <Link to="/community">
  <button className="back-btn">← Back to Community</button>
</Link>

      {/* THREAD HEADER */}
      <div className="thread-card">
        <h2 className="thread-title">{post.title}</h2>

        <p className="thread-meta">
           Posted by <strong>{post.username || "User"}</strong> •{" "}
          {getReplyTime(post)
           ? getReplyTime(post).toLocaleString()
           : "Just now"} •{" "}
        <span className="thread-category">{post.category}</span>
      </p>

        <p className="thread-body">{post.body}</p>

        {/* DELETE THREAD BUTTON (Author Only) */}
        {auth.currentUser?.uid === post.userId && (
          <button className="thread-delete-btn" onClick={deleteThread}>
            🗑 Delete Post
          </button>
        )}

        <Reactions postId={id} post={post} />
      </div>

      {/* REPLIES */}
      <h3 className="reply-header">Replies</h3>

      <div className="reply-list">
        {loading ? (
          <p className="forum-loading">Loading replies...</p>
        ) : replies.length === 0 ? (
          <p className="no-replies">No replies yet — start the discussion!</p>
        ) : (
          localReplies.map((r) => (

            <div
              key={r.id}
              className={r.isAI ? "reply-bubble-ai" : "reply-bubble"}
            >
              <p className="bubble-text">{r.text}</p>

          
              <div className="bubble-meta">
                <span
                  className="username-mention"
                  onClick={() => setReplyText(`@${r.username} `)}
                >
                  @{r.username || "User"}
                </span>

                <span className="bubble-meta-time">
                  •{" "}
                  {getReplyTime(r)
                  ? getReplyTime(r).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })
                  : "just now"}
                </span>

                {/* DELETE REPLY */}
                {auth.currentUser?.uid === r.userId && (
                  <button
                    className="reply-delete-btn"
                    onClick={() => deleteReply(r.id)}
                  >
                    🗑
                  </button>
                )}
              </div>

              {/* REACTIONS */}
              {/* REACTIONS */}
        <div className="reply-reactions">
        {["👍", "😂", "❤️", "🔥", "👏"].map((emoji) => {
        const key = reactionMap[emoji];
        const userReaction = r.userReactions?.[auth.currentUser?.uid];
        const locked = !!userReaction;

        return (
        <span
        key={emoji}
        className={`reply-react-btn ${
          userReaction === key ? "reacted" : locked ? "disabled" : ""
        }`}
        onClick={() => addReplyReaction(r.id, emoji)}
      >
        {emoji} {r.replyReactions?.[key] || ""}
        </span>
        );
      })}
      </div>

            </div>
          ))
        )}
      </div>

      {/* LOAD MORE */}
      {lastDoc && (
        <button className="load-more-btn" onClick={() => loadReplies()}>
          {loadingMore ? "Loading..." : "Load More Replies"}
        </button>
      )}

      {/* REPLY BOX */}
      <div className="reply-box">
        <textarea
          className="reply-input"
          placeholder="Write a reply..."
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
        ></textarea>

        <button className="reply-btn" onClick={submitReply}>
          Post Reply
        </button>
      </div>
    </div>
  );
}

export default Thread;

